/**
 * Portfolio Tracker — ProfitSpot
 * Analyze any wallet's LP positions, see running IL risk,
 * and get AI-powered "Should I Exit?" recommendations.
 */
import React, { useState, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { getBackendBaseUrl } from '../lib/apiBase';
import {
  Wallet, Search, RefreshCw, Brain, TrendingDown,
  AlertTriangle, CheckCircle2, X, ChevronDown,
  ExternalLink, Shield, Coins, Lock,
  BarChart2, Target, Copy, Info, Calculator, ShieldAlert, ShieldCheck, Users, Crown
} from 'lucide-react';

const API_URL = getBackendBaseUrl();

const CHAIN_OPTIONS = [
  { id: 'eth',       label: 'Ethereum',  color: '#627EEA' },
  { id: 'arbitrum',  label: 'Arbitrum',  color: '#28A0F0' },
  { id: 'polygon',   label: 'Polygon',   color: '#8247E5' },
  { id: 'optimism',  label: 'Optimism',  color: '#FF0420' },
  { id: 'base',      label: 'Base',      color: '#0052FF' },
  { id: 'bsc',       label: 'BNB Chain', color: '#F0B90B' },
  { id: 'avalanche', label: 'Avalanche', color: '#E84142' },
  { id: 'fantom',    label: 'Fantom',    color: '#1969FF' },
  { id: 'zksync',    label: 'zkSync',    color: '#8B5CF6' },
  { id: 'linea',     label: 'Linea',     color: '#61DFFF' },
];

const EXAMPLE_ADDRESSES = [
  { label: 'Vitalik.eth', address: '0xd8dA6BF26964aF9D7eEd9e03E53415D37AA96045' },
  { label: 'Wintermute', address: '0xDa9dfa130Df4dE4673b89022EE50ff26f6EA73Cf' },
];

function formatUSD(val) {
  if (!val && val !== 0) return '—';
  if (val >= 1e6) return `$${(val / 1e6).toFixed(2)}M`;
  if (val >= 1e3) return `$${(val / 1e3).toFixed(1)}K`;
  return `$${val.toFixed(2)}`;
}

// ─── IL Risk Badge ─────────────────────────────────────────
const ILBadge = ({ il }) => (
  <span
    className="text-[10px] px-2 py-0.5 rounded font-bold border cursor-help"
    style={{ color: il.color, borderColor: il.color + '40', backgroundColor: il.color + '15' }}
    title={il.note || `IL Risk: ${il.level} — ${il.range}`}
  >
    IL: {il.level}
  </span>
);

// ─── Verdict Badge ──────────────────────────────────────────
const VerdictBadge = ({ verdict }) => {
  const styles = {
    'EXIT NOW':        'bg-red-500/20 text-red-400 border-red-500/40',
    'HOLD':            'bg-[#00ff88]/20 text-[#00ff88] border-[#00ff88]/40',
    'REDUCE EXPOSURE': 'bg-[#ffa502]/20 text-[#ffa502] border-[#ffa502]/40',
  };
  return (
    <span className={`text-xs font-bold px-3 py-1 rounded-full border ${styles[verdict] || 'bg-gray-700 text-gray-300 border-gray-600'}`}>
      {verdict}
    </span>
  );
};

// ─── AI Recommendation Card ──────────────────────────────────
const AIRecommendationCard = ({ rec, onClose }) => {
  const riskColor = rec.risk_score >= 7 ? '#ff4757' : rec.risk_score >= 4 ? '#ffa502' : '#00ff88';
  return (
    <div className="mt-3 bg-[#0a0a12] border border-[#00d4ff]/30 rounded-xl p-4 space-y-3" data-testid="ai-recommendation-card">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Brain className="w-4 h-4 text-[#00d4ff]" />
          <span className="text-xs font-bold text-[#00d4ff] uppercase tracking-wider">AI Recommendation</span>
        </div>
        <button onClick={onClose} className="text-gray-600 hover:text-white"><X className="w-3.5 h-3.5" /></button>
      </div>
      <div className="flex items-center gap-3">
        <VerdictBadge verdict={rec.verdict} />
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-gray-500">Risk Score</span>
          <span className="text-sm font-bold" style={{ color: riskColor }}>{rec.risk_score}/10</span>
        </div>
      </div>
      {rec.reason && <p className="text-sm text-gray-300 leading-relaxed">{rec.reason}</p>}
      {rec.il_context && (
        <div className="bg-[#ffa502]/5 border border-[#ffa502]/20 rounded-lg p-3">
          <p className="text-[10px] text-[#ffa502] uppercase tracking-wider mb-1">IL Context</p>
          <p className="text-xs text-gray-400">{rec.il_context}</p>
        </div>
      )}
      {rec.action_steps?.length > 0 && (
        <div>
          <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-2">Before You Decide</p>
          <ul className="space-y-1.5">
            {rec.action_steps.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-gray-400">
                <Target className="w-3.5 h-3.5 text-[#00d4ff] mt-0.5 flex-shrink-0" />
                {step}
              </li>
            ))}
          </ul>
        </div>
      )}
      <p className="text-[10px] text-gray-600">Powered by AI · Not financial advice</p>
    </div>
  );
};

// ─── IL Calculator (Exact — requires entry prices) ────────────
// CoinGecko token IDs (shared with SimulatorLabs)
const CG_IDS = { ETH:'ethereum',BTC:'bitcoin',SOL:'solana',ARB:'arbitrum',OP:'optimism',MATIC:'matic-network',AVAX:'avalanche-2',BNB:'binancecoin',LINK:'chainlink',UNI:'uniswap',USDC:'usd-coin',USDT:'tether',DAI:'dai',FRAX:'frax',WETH:'ethereum',WBTC:'bitcoin' };
const STABLES = new Set(['USDC','USDT','DAI','FRAX','BUSD','LUSD','crvUSD']);

const fetchCGPrice = async (sym) => {
  const id = CG_IDS[sym?.toUpperCase()];
  if (!id) return null;
  try {
    const r = await fetch(`https://api.coingecko.com/api/v3/simple/price?ids=${id}&vs_currencies=usd`,{cache:'no-store'});
    if(r.ok){const d=await r.json();return d[id]?.usd??null;}
  }catch{return null;}
  return null;
};

const ExactILCalculator = ({ position }) => {
  const [entryA, setEntryA] = useState('');
  const [entryB, setEntryB] = useState('');
  const [currentA, setCurrentA] = useState('');
  const [currentB, setCurrentB] = useState('');
  const [result, setResult] = useState(null);
  const [fetching, setFetching] = useState(null);

  const tokenCount = position.tokens?.length || 0;
  const tokenA = position.tokens[0]?.symbol || 'Token A';
  const tokenB = position.tokens[1]?.symbol || 'Token B';

  // 3+ token pools: IL formula only applies to 2-token constant-product AMMs
  if (tokenCount > 2) {
    const tokenNames = position.tokens.map(t => t.symbol).join(', ');
    return (
      <div className="mt-3 bg-[#0a0a12] border border-[#ffa502]/20 rounded-xl p-4" data-testid="exact-il-calculator">
        <div className="flex items-center gap-2 mb-3">
          <Calculator className="w-4 h-4 text-[#ffa502]" />
          <span className="text-xs font-bold text-[#ffa502] uppercase tracking-wider">Exact IL Calculator</span>
        </div>
        <div className="text-center py-4 space-y-2">
          <p className="text-sm text-gray-300">This is a <span className="text-white font-bold">{tokenCount}-token pool</span> ({tokenNames})</p>
          <p className="text-xs text-gray-500">The exact IL calculator uses the standard 2-token constant-product formula (2√k / (1+k) − 1) which does not apply to multi-token pools.</p>
          <p className="text-xs text-gray-500">For {tokenCount}-token pools (like Curve 3pool), impermanent loss is typically very low when all tokens maintain their peg.</p>
          <div className="mt-3 p-2.5 bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-lg">
            <p className="text-xs text-[#00ff88]">ℹ️ Use the IL Calculator on pool cards (Yield Explorer) for slider-based IL estimation on any pool.</p>
          </div>
        </div>
      </div>
    );
  }

  const fetchPrice = async (sym, setter, field) => {
    // Stablecoins are always $1.00
    if (STABLES.has(sym.toUpperCase())) {
      setter('1.00');
      toast.success(`${sym} is a stablecoin — price set to $1.00`);
      return;
    }
    setFetching(field);
    const p = await fetchCGPrice(sym);
    if (p) { setter(String(p)); toast.success(`${sym} live price: $${p.toLocaleString()}`); }
    else toast.error(`No live price for ${sym} — enter manually`);
    setFetching(null);
  };

  const checkStable = (sym, val) => {
    if (STABLES.has(sym.toUpperCase()) && Math.abs(parseFloat(val) - 1.0) > 0.05)
      toast.warning(`${sym} is pegged to $1.00 — your price ($${val}) looks unusual. Is that right?`, {duration:6000});
  };

  const calculate = () => {
    const eA = parseFloat(entryA), eB = parseFloat(entryB);
    const cA = parseFloat(currentA), cB = parseFloat(currentB);
    if (!eA || !eB || isNaN(eA) || isNaN(eB)) { toast.error(`Enter entry prices for both ${tokenA} and ${tokenB}`); return; }
    if (!cA || !cB || isNaN(cA) || isNaN(cB)) { toast.error(`Enter current prices for both ${tokenA} and ${tokenB} — use today's market price`); return; }
    checkStable(tokenA, currentA);
    checkStable(tokenB, currentB);
    const k = (cA / cB) / (eA / eB);
    const il = (2 * Math.sqrt(k) / (1 + k)) - 1;
    const ilPct = il * 100;
    const ilUSD = position.value_usd * Math.abs(ilPct / 100);
    const priceChange = ((cA / eA) - 1) * 100;
    setResult({ ilPct: ilPct.toFixed(2), ilUSD: ilUSD.toFixed(2), priceChangePct: priceChange.toFixed(1), k: k.toFixed(3) });
  };

  const reset = () => { setResult(null); setEntryA(''); setEntryB(''); setCurrentA(''); setCurrentB(''); };

  return (
    <div className="mt-3 bg-[#0a0a12] border border-[#ffa502]/20 rounded-xl p-4" data-testid="exact-il-calculator">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-4 h-4 text-[#ffa502]" />
          <span className="text-xs font-bold text-[#ffa502] uppercase tracking-wider">Exact IL Calculator</span>
          <FeatureInfo featureKey="il_calculator" size="xs" className="ml-1 inline-flex" />
        </div>
        {result && (
          <button onClick={reset} className="flex items-center gap-1 text-[10px] text-gray-500 hover:text-white transition-colors" data-testid="il-calc-reset">
            <RefreshCw className="w-3 h-3" /> Reset
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mb-3">
        {/* Entry A — historical, user knows this */}
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">{tokenA} entry ($)</label>
          <input type="number" value={entryA} onChange={e=>setEntryA(e.target.value)} placeholder="e.g. 1800"
            className="w-full px-2 py-1.5 bg-[#0f0f1a] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/50"
            data-testid="entry-price-token-a" />
        </div>
        {/* Entry B — historical, user knows this */}
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">{tokenB} entry ($)</label>
          <input type="number" value={entryB} onChange={e=>setEntryB(e.target.value)} placeholder="e.g. 1.00"
            className="w-full px-2 py-1.5 bg-[#0f0f1a] border border-gray-700 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/50"
            data-testid="entry-price-token-b" />
        </div>
        {/* Current A */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] text-[#ffa502]/70">{tokenA} now ($) *</label>
            <button onClick={() => fetchPrice(tokenA, setCurrentA, 'cA')} disabled={fetching==='cA'} className="text-[9px] text-[#00ff88] font-bold hover:underline disabled:opacity-50">
              {fetching==='cA'?'...':'↗ live'}
            </button>
          </div>
          <input type="number" value={currentA} onChange={e=>setCurrentA(e.target.value)} placeholder="Today's price"
            className="w-full px-2 py-1.5 bg-[#0f0f1a] border border-[#ffa502]/30 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/60"
            data-testid="current-price-token-a" />
        </div>
        {/* Current B */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[10px] text-[#ffa502]/70">{tokenB} now ($) *</label>
            <button onClick={() => fetchPrice(tokenB, setCurrentB, 'cB')} disabled={fetching==='cB'} className="text-[9px] text-[#00ff88] font-bold hover:underline disabled:opacity-50">
              {fetching==='cB'?'...':'↗ live'}
            </button>
          </div>
          <input type="number" value={currentB} onChange={e=>setCurrentB(e.target.value)} placeholder="Today's price"
            className="w-full px-2 py-1.5 bg-[#0f0f1a] border border-[#ffa502]/30 rounded text-xs text-white focus:outline-none focus:border-[#ffa502]/60"
            data-testid="current-price-token-b" />
        </div>
      </div>
      <button onClick={calculate}
        className="w-full py-2 bg-[#ffa502]/20 text-[#ffa502] border border-[#ffa502]/30 rounded-lg text-xs font-bold hover:bg-[#ffa502]/30 transition-all"
        data-testid="calculate-il-btn">
        Calculate Exact IL
      </button>
      {result && (
        <div className="mt-3 grid grid-cols-2 gap-2" data-testid="il-result">
          <div className="bg-[#0f0f1a] rounded-lg p-2.5 text-center">
            <p className="text-[10px] text-gray-500 mb-1">Impermanent Loss</p>
            <p className={`text-sm font-bold ${parseFloat(result.ilPct) < -2 ? 'text-red-400' : 'text-[#ffa502]'}`}>{result.ilPct}%</p>
          </div>
          <div className="bg-[#0f0f1a] rounded-lg p-2.5 text-center">
            <p className="text-[10px] text-gray-500 mb-1">USD Loss to IL</p>
            <p className="text-sm font-bold text-red-400">-${result.ilUSD}</p>
          </div>
          <div className="bg-[#0f0f1a] rounded-lg p-2.5 text-center col-span-2">
            <p className="text-[10px] text-gray-500 mb-1">{tokenA} change since entry</p>
            <p className={`text-sm font-bold ${parseFloat(result.priceChangePct)>=0?'text-[#00ff88]':'text-red-400'}`}>{parseFloat(result.priceChangePct)>=0?'+':''}{result.priceChangePct}%</p>
          </div>
        </div>
      )}
      <p className="text-[10px] text-gray-700 mt-2 text-center">Formula: 2√k/(1+k)−1 · click <span className="text-[#00ff88]">↗ live</span> on current prices to auto-fill</p>
    </div>
  );
};
const PositionCard = ({ position, canUseAI }) => {
  const [expanded, setExpanded] = useState(false);
  const [aiRec, setAIRec] = useState(null);
  const [aiLoading, setAILoading] = useState(false);
  const [showILCalc, setShowILCalc] = useState(false);

  const handleAIAnalyze = async (e) => {
    e.stopPropagation();
    if (aiRec) { setAIRec(null); return; }
    setAILoading(true);
    try {
      const res = await fetch(`${API_URL}/api/portfolio/ai-analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          address: position.full_address || '',
          protocol: position.protocol,
          pair: position.pair,
          position_type: position.type,
          value_usd: position.value_usd,
          chain: position.chain,
          il_estimate: position.il_estimate,
          tokens: position.tokens,
        }),
      });
      if (res.ok) {
        const data = await res.json();
        if (data.configured && data.recommendation) {
          setAIRec(data.recommendation);
        } else {
          toast.info(data.message || 'AI insights not configured');
        }
      }
    } catch (e) {
      toast.error('AI analysis failed');
    }
    setAILoading(false);
  };

  return (
    <div
      className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-4 hover:border-gray-700 transition-all cursor-pointer"
      onClick={() => setExpanded(!expanded)}
      data-testid={`position-card-${position.protocol.toLowerCase().replace(/\s/g, '-')}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-bold text-white text-sm">{position.pair}</span>
            <ILBadge il={position.il_estimate} />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500">{position.protocol}</span>
            <span className="text-[10px] text-gray-700">·</span>
            <span className="text-xs text-gray-500">{position.type}</span>
            <span className="text-[10px] text-gray-700">·</span>
            <span className="text-xs text-gray-500">{position.chain}</span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-lg font-bold text-white">{formatUSD(position.value_usd)}</p>
          <p className="text-[10px] text-gray-500 mt-0.5">{position.il_estimate.range} IL range</p>
        </div>
      </div>

      {/* Token breakdown */}
      {position.tokens?.length > 0 && (
        <div className="mt-3 flex gap-2 flex-wrap">
          {position.tokens.map((t, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-gray-900/50 px-2 py-1 rounded-lg">
              <Coins className="w-3 h-3 text-gray-500" />
              <span className="text-xs text-gray-300 font-medium">{t.symbol}</span>
              <span className="text-xs text-gray-600">{formatUSD(t.value_usd)}</span>
            </div>
          ))}
        </div>
      )}

      {/* IL note — always visible as subtle info line */}
      {position.il_estimate?.note && (
        <div className="flex items-start gap-1.5 mt-2">
          <p className="text-[10px] text-gray-600 pl-0.5 leading-relaxed flex-1" data-testid={`il-note-${position.protocol.toLowerCase().replace(/\s/g, '-')}`}>
            {position.il_estimate.note}
          </p>
          <FeatureInfo featureKey="il_position" size="xs" className="mt-0.5 flex-shrink-0" />
        </div>
      )}

      {/* Action row */}
      <div className="mt-3 flex items-center justify-between pt-3 border-t border-gray-800">
        <div className="flex items-center gap-2">
          {canUseAI ? (
            <button
              onClick={handleAIAnalyze}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#00d4ff]/10 text-[#00d4ff] hover:bg-[#00d4ff]/20 text-xs font-medium transition-all"
              data-testid={`ai-exit-btn-${position.protocol.toLowerCase().replace(/\s/g, '-')}`}
            >
              {aiLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Brain className="w-3.5 h-3.5" />}
              {aiRec ? 'Hide AI' : 'Should I Exit?'}
            </button>
          ) : (
            <div
              title="AI Exit Analysis — Pro ($29/mo) or Elite ($79/mo) feature. Upgrade to unlock."
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-800/50 text-gray-600 text-xs font-medium cursor-not-allowed border border-gray-700/50"
              data-testid={`ai-exit-locked-${position.protocol.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Lock className="w-3.5 h-3.5" />
              AI Exit Analysis
              <span className="text-[10px] text-[#00d4ff]/60 bg-[#00d4ff]/10 px-1 rounded">Pro</span>
            </div>
          )}
          {position.tokens?.length >= 2 && (
            <button
              onClick={(e) => { e.stopPropagation(); setShowILCalc(!showILCalc); }}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#ffa502]/10 text-[#ffa502] hover:bg-[#ffa502]/20 text-xs font-medium transition-all"
              data-testid={`il-calc-btn-${position.protocol.toLowerCase().replace(/\s/g, '-')}`}
            >
              <Calculator className="w-3.5 h-3.5" />
              {showILCalc ? 'Hide IL' : 'Exact IL'}
            </button>
          )}
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); setExpanded(!expanded); }}
          className="flex items-center gap-1 text-xs text-gray-500 hover:text-white transition-colors"
        >
          <ChevronDown className={`w-4 h-4 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Exact IL Calculator */}
      {showILCalc && position.tokens?.length >= 2 && (
        <ExactILCalculator position={position} />
      )}

      {/* AI Recommendation */}
      {aiRec && <AIRecommendationCard rec={aiRec} onClose={() => setAIRec(null)} />}
    </div>
  );
};

// ─── Main Portfolio Tracker Page ──────────────────────────────
import { FeatureInfo } from '../components/FeatureInfo';

export default function PortfolioTracker() {
  const { token, user } = useAuth();
  const userTier = user?.subscription?.tier || 'free';
  const isAdmin = user?.role === 'admin';
  const canUseAI = userTier === 'pro' || userTier === 'unlimited' || userTier === 'elite' || isAdmin;
  const isPremium = userTier === 'pro' || userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  const isElite = userTier === 'elite' || userTier === 'unlimited' || isAdmin;
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [walletData, setWalletData] = useState(null);
  const [error, setError] = useState('');

  // De.Fi Shield — wallet approval risk scanning
  const [approvalsData, setApprovalsData] = useState(null);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [showApprovals, setShowApprovals] = useState(false);
  const [riskAnalysis, setRiskAnalysis] = useState(null);
  const [riskLoading, setRiskLoading] = useState(false);

  const fetchSingleChain = useCallback(async (target, chain) => {
    const res = await fetch(
      `${API_URL}/api/portfolio/wallet?address=${encodeURIComponent(target)}&chain=${chain}`,
      { headers: token ? { Authorization: `Bearer ${token}` } : {} }
    );
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.detail || 'Failed to fetch');
    }
    return res.json();
  }, [token]);

  const handleAnalyze = async (addressOverride) => {
    const target = (addressOverride || address).trim();
    if (!target) { toast.error('Enter a wallet address'); return; }
    if (!target.startsWith('0x') && !target.endsWith('.eth')) {
      toast.error('Enter a valid EVM wallet address (0x...)');
      return;
    }
    setLoading(true);
    setError('');
    setWalletData(null);
    try {
      const results = await Promise.allSettled(
        CHAIN_OPTIONS.map(c => fetchSingleChain(target, c.id))
      );
      const allPositions = [];
      let totalValue = 0;
      const chainBreakdown = {};
      results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
          const d = result.value;
          const chainName = CHAIN_OPTIONS[i].label;
          const positions = (d.positions || []).map(p => ({ ...p, chain: chainName, chain_id: CHAIN_OPTIONS[i].id }));
          allPositions.push(...positions);
          totalValue += d.total_value_usd || 0;
          if (positions.length > 0) chainBreakdown[chainName] = { count: positions.length, value: d.total_value_usd || 0 };
        }
      });
      const sorted = allPositions.sort((a, b) => b.value_usd - a.value_usd);
      setWalletData({
        address: target.slice(0, 6) + '...' + target.slice(-4),
        full_address: target,
        chain: 'All Chains',
        chain_id: 'all',
        total_value_usd: totalValue,
        position_count: sorted.length,
        positions: sorted,
        chain_breakdown: chainBreakdown,
        data_source: sorted.some(p => p.source === 'mock') ? 'mock' : 'moralis',
        fetched_at: new Date().toISOString(),
      });
      if (sorted.length === 0) toast.info('No DeFi positions found across any chain');
      else toast.success(`Found ${sorted.length} positions across ${Object.keys(chainBreakdown).length} chains`);
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
    }
    setLoading(false);
  };

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(walletData?.full_address || address);
    toast.success('Address copied');
  };

  // Fetch portfolio risk analysis (Feature 5) — Pro+ only
  const fetchRiskAnalysis = useCallback(async () => {
    if (!walletData?.full_address || !isPremium) return;
    setRiskLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/portfolio/risk-analysis/${encodeURIComponent(walletData.full_address)}?chain=eth`,
        { headers: token ? { Authorization: `Bearer ${token}` } : {} }
      );
      if (res.ok) {
        const data = await res.json();
        setRiskAnalysis(data);
      }
    } catch (e) {
      console.warn('Risk analysis failed:', e);
    }
    setRiskLoading(false);
  }, [walletData, token, isPremium]);

  // Auto-fetch risk analysis when wallet data changes
  React.useEffect(() => {
    if (walletData && walletData.positions?.length > 0) {
      fetchRiskAnalysis();
    } else {
      setRiskAnalysis(null);
    }
  }, [walletData, fetchRiskAnalysis]);

  const handleScanApprovals = async () => {
    const target = walletData?.full_address || address;
    if (!target) return;
    setApprovalsLoading(true);
    setShowApprovals(true);
    try {
      const results = await Promise.allSettled(
        CHAIN_OPTIONS.map(c =>
          fetch(`${API_URL}/api/portfolio/approvals?address=${encodeURIComponent(target)}&chain=${c.id}`, {
            headers: token ? { Authorization: `Bearer ${token}` } : {},
          }).then(r => r.json())
        )
      );
      const merged = {
        configured: false,
        approvals: [],
        risk_summary: { total_approvals: 0, unlimited_approvals: 0, high_risk_count: 0, risk_level: 'LOW' },
      };
      for (const r of results) {
        if (r.status !== 'fulfilled') continue;
        const d = r.value;
        if (d.configured) merged.configured = true;
        merged.approvals.push(...(d.approvals || []));
        const rs = d.risk_summary;
        if (rs) {
          merged.risk_summary.total_approvals += rs.total_approvals || 0;
          merged.risk_summary.unlimited_approvals += rs.unlimited_approvals || 0;
          merged.risk_summary.high_risk_count += rs.high_risk_count || 0;
        }
      }
      merged.risk_summary.risk_level =
        merged.risk_summary.high_risk_count > 0 ? 'HIGH'
          : merged.risk_summary.unlimited_approvals > 0 ? 'MEDIUM' : 'LOW';
      setApprovalsData(merged);
      if (merged.risk_summary?.high_risk_count > 0) {
        toast.warning(`Found ${merged.risk_summary.high_risk_count} high-risk approvals (all chains)!`);
      } else if (merged.configured) {
        toast.success('Approval scan complete');
      }
    } catch (e) {
      toast.error('Approval scan failed');
    }
    setApprovalsLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#0a0a12] text-white" data-testid="portfolio-tracker-page">
      <div className="max-w-3xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-xl bg-[#00d4ff]/10 border border-[#00d4ff]/30 flex items-center justify-center">
              <Wallet className="w-5 h-5 text-[#00d4ff]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold text-white">Portfolio Analyzer</h1>
                <FeatureInfo featureKey="portfolio_tracker" />
              </div>
              <p className="text-sm text-gray-400">Tracks DeFi LP (Liquidity Provider) positions only — not total wallet balance</p>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-4 mb-6" data-testid="portfolio-search-section">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Address input */}
            <div className="flex-1 relative">
              <Wallet className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
              <input
                type="text"
                value={address}
                onChange={e => setAddress(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleAnalyze()}
                placeholder="Enter wallet address (0x...)"
                className="w-full pl-10 pr-3 py-3 bg-[#0a0a12] border border-gray-700 rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#00d4ff]/50 focus:ring-1 focus:ring-[#00d4ff]/20"
                data-testid="portfolio-address-input"
              />
            </div>

            {/* Analyze button */}
            <button
              onClick={() => handleAnalyze()}
              disabled={loading}
              className="flex items-center justify-center gap-2 px-5 py-3 bg-[#00d4ff] text-black font-bold rounded-lg hover:bg-[#00bbee] transition-colors disabled:opacity-50 whitespace-nowrap"
              data-testid="portfolio-analyze-btn"
            >
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
              {loading ? 'Scanning...' : 'Analyze'}
            </button>
          </div>

          {/* Multi-chain toggle + example addresses */}
          <div className="mt-3 flex items-center flex-wrap gap-2">
            <span className="text-[10px] text-gray-500">Scans all supported chains automatically.</span>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[10px] text-gray-600">Try:</span>
              {EXAMPLE_ADDRESSES.map(ex => (
                <button
                  key={ex.address}
                  onClick={() => { setAddress(ex.address); handleAnalyze(ex.address); }}
                  className="text-[10px] text-[#00d4ff]/70 hover:text-[#00d4ff] px-2 py-0.5 bg-[#00d4ff]/5 hover:bg-[#00d4ff]/10 rounded border border-[#00d4ff]/20 transition-colors"
                  data-testid={`example-address-${ex.label.toLowerCase().replace(/\s/g, '-')}`}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0" />
            <p className="text-sm text-red-400">{error}</p>
          </div>
        )}

        {/* Results */}
        {walletData && (
          <div className="space-y-4">
            {/* Wallet Summary */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-4" data-testid="wallet-summary">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-bold text-white font-mono">{walletData.address}</p>
                    <button onClick={handleCopyAddress} className="text-gray-500 hover:text-white" data-testid="copy-address-btn">
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <a
                      href={`https://etherscan.io/address/${walletData.full_address}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-gray-500 hover:text-[#00d4ff]"
                      data-testid="etherscan-link"
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5">{walletData.chain} · {walletData.position_count} positions found</p>
                </div>
                <div className="flex items-center gap-2">
                  {walletData.data_source === 'mock' && (
                    <span className="text-[10px] px-2 py-0.5 bg-[#ffa502]/15 text-[#ffa502] border border-[#ffa502]/30 rounded font-bold">
                      PREVIEW DATA
                    </span>
                  )}
                  <button
                    onClick={() => { setWalletData(null); setAddress(''); setError(''); setApprovalsData(null); setShowApprovals(false); }}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-800 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg text-xs font-medium transition-all"
                    data-testid="clear-data-btn"
                  >
                    <X className="w-3.5 h-3.5" />
                    Clear Data
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-[#0a0a12] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">LP Position Value</p>
                  <p className="text-xl font-bold text-white" data-testid="portfolio-total-value">{formatUSD(walletData.total_value_usd)}</p>
                </div>
                <div className="bg-[#0a0a12] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Positions</p>
                  <p className="text-xl font-bold text-white" data-testid="portfolio-position-count">{walletData.position_count}</p>
                </div>
                <div className="bg-[#0a0a12] rounded-lg p-3 text-center">
                  <p className="text-[10px] text-gray-500 uppercase tracking-wider mb-1">Chain</p>
                  <p className="text-sm font-bold" style={{ color: '#00d4ff' }}>{walletData.chain}</p>
                </div>
              </div>
              {/* Chain breakdown for multi-chain */}
              {walletData.chain_breakdown && Object.keys(walletData.chain_breakdown).length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2" data-testid="chain-breakdown">
                  {Object.entries(walletData.chain_breakdown).map(([chain, info]) => (
                    <div key={chain} className="flex items-center gap-1.5 bg-[#0a0a12] px-2.5 py-1.5 rounded-lg border border-gray-800">
                      <span className="text-xs font-medium text-gray-300">{chain}</span>
                      <span className="text-[10px] text-gray-500">{info.count} pos</span>
                      <span className="text-[10px] font-bold text-[#00ff88]">{formatUSD(info.value)}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Portfolio Risk Grade (Feature 5) — Pro+ basic grades, Elite optimization */}
            {isPremium && (riskAnalysis || riskLoading) && (
              <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-4" data-testid="portfolio-risk-grade">                <div className="flex items-center gap-2 mb-3">
                  <Shield className="w-4 h-4 text-[#8b5cf6]" />
                  <span className="text-sm font-bold text-white">Portfolio Health</span>
                  <FeatureInfo featureKey="portfolio_risk_analysis" />
                </div>
                {riskLoading ? (
                  <div className="flex items-center gap-2 text-gray-500 text-xs">
                    <RefreshCw className="w-4 h-4 animate-spin" /> Analyzing portfolio risk...
                  </div>
                ) : riskAnalysis ? (
                  <div>
                    <div className="flex items-center gap-4 mb-4">
                      {/* Grade Badge */}
                      <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl font-black text-white shadow-lg ${
                        { A: 'bg-emerald-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', F: 'bg-red-500' }[riskAnalysis.portfolio_grade] || 'bg-gray-500'
                      }`} data-testid="portfolio-grade-badge">
                        {riskAnalysis.portfolio_grade}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-white">Score: {riskAnalysis.portfolio_score}/100</span>
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-gray-400">
                          <span>Value: {formatUSD(riskAnalysis.total_value)}</span>
                          <span>Annual Yield: {formatUSD(riskAnalysis.total_annual_yield)}</span>
                        </div>
                        {riskAnalysis.total_optimization_potential > 0 && isElite && (
                          <div className="mt-1 text-xs font-bold text-[#00ff88]" data-testid="optimization-potential">
                            Optimization potential: +{formatUSD(riskAnalysis.total_optimization_potential)}/yr
                          </div>
                        )}
                        {riskAnalysis.total_optimization_potential > 0 && !isElite && (
                          <div className="mt-1 text-xs text-gray-500">
                            <Crown className="w-3 h-3 inline mr-1 text-[#ffa502]" />
                            Upgrade to Elite to see optimization suggestions
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Per-position breakdown */}
                    {riskAnalysis.positions?.length > 0 && (
                      <div className="space-y-2 mt-3">
                        <div className="text-[10px] text-gray-500 uppercase tracking-wider">Position Risk Breakdown</div>
                        {riskAnalysis.positions.map((pos, i) => (
                          <div key={i} className="bg-[#0a0a12] rounded-lg p-3 border border-gray-800">
                            <div className="flex items-center justify-between mb-1">
                              <div className="flex items-center gap-2">
                                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white ${
                                  { A: 'bg-emerald-500', B: 'bg-blue-500', C: 'bg-yellow-500', D: 'bg-orange-500', F: 'bg-red-500' }[pos.risk_grade] || 'bg-gray-500'
                                }`}>{pos.risk_grade}</div>
                                <span className="text-xs font-medium text-white">{pos.pool}</span>
                              </div>
                              <span className="text-xs text-gray-400">{formatUSD(pos.value)}</span>
                            </div>
                            <div className="flex items-center gap-3 text-[10px] text-gray-500">
                              <span>{pos.chain}</span>
                              <span>APY: {pos.current_apy?.toFixed(1)}%</span>
                            </div>
                            {isElite && pos.better_alternatives?.length > 0 && (
                              <div className="mt-2 p-2 bg-[#00ff88]/5 border border-[#00ff88]/20 rounded text-[10px]">
                                <span className="text-[#00ff88] font-bold">Switch to {pos.better_alternatives[0].pool} for {pos.better_alternatives[0].apy}% APY (+{formatUSD(pos.better_alternatives[0].extra_annual_yield)}/yr)</span>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ) : null}
              </div>
            )}

            {/* De.Fi Shield — Token Approval Risk Scanner */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-4" data-testid="approvals-section">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldAlert className="w-4 h-4 text-[#ffa502]" />
                  <span className="text-sm font-bold text-white">Token Approval Risk</span>
                  <span className="text-[10px] text-gray-500">Powered by De.Fi Shield</span>
                </div>
                <button
                  onClick={handleScanApprovals}
                  disabled={approvalsLoading}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-[#ffa502]/15 border border-[#ffa502]/30 text-[#ffa502] text-xs font-bold rounded-lg hover:bg-[#ffa502]/25 transition-colors disabled:opacity-50"
                  data-testid="scan-approvals-btn"
                >
                  {approvalsLoading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
                  {approvalsLoading ? 'Scanning...' : 'Scan Approvals'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Detect unlimited ERC20 approvals — risky if a protocol gets exploited.
              </p>

              {showApprovals && approvalsData && (
                <div className="mt-3 space-y-2">
                  {!approvalsData.configured ? (
                    <div className="bg-gray-900/50 rounded-lg p-3 text-xs text-gray-400">
                      De.Fi API key not configured. Set <code className="text-[#ffa502]">DEFI_API_KEY</code> in backend/.env to enable.
                    </div>
                  ) : (
                    <>
                      {/* Risk summary bar */}
                      {approvalsData.risk_summary && (
                        <div className="flex items-center gap-3 bg-[#0a0a12] rounded-lg p-3">
                          {approvalsData.risk_summary.risk_level === 'HIGH' ? (
                            <ShieldAlert className="w-5 h-5 text-red-400 flex-shrink-0" />
                          ) : approvalsData.risk_summary.risk_level === 'MEDIUM' ? (
                            <AlertTriangle className="w-5 h-5 text-[#ffa502] flex-shrink-0" />
                          ) : (
                            <ShieldCheck className="w-5 h-5 text-[#00ff88] flex-shrink-0" />
                          )}
                          <div className="flex-1">
                            <p className="text-xs font-bold text-white">
                              {approvalsData.risk_summary.total_approvals} approvals found
                              {approvalsData.risk_summary.unlimited_approvals > 0 && (
                                <span className="ml-2 text-red-400">· {approvalsData.risk_summary.unlimited_approvals} unlimited</span>
                              )}
                            </p>
                            <p className="text-[10px] text-gray-500">
                              Risk Level: <span className={approvalsData.risk_summary.risk_level === 'HIGH' ? 'text-red-400 font-bold' : approvalsData.risk_summary.risk_level === 'MEDIUM' ? 'text-[#ffa502] font-bold' : 'text-[#00ff88] font-bold'}>{approvalsData.risk_summary.risk_level}</span>
                            </p>
                          </div>
                          {approvalsData.risk_summary.total_approvals > 0 && (
                            <a
                              href={approvalsData.revoke_url || 'https://revoke.cash'}
                              target="_blank" rel="noopener noreferrer"
                              className="flex items-center gap-1 px-2.5 py-1 bg-red-500/15 border border-red-500/30 text-red-400 text-[10px] font-bold rounded-lg hover:bg-red-500/25 transition-colors"
                              data-testid="revoke-cash-link"
                            >
                              Revoke <ExternalLink className="w-2.5 h-2.5" />
                            </a>
                          )}
                        </div>
                      )}

                      {/* Approval list */}
                      {approvalsData.approvals?.length > 0 ? (
                        <div className="space-y-1.5 max-h-56 overflow-y-auto">
                          {approvalsData.approvals.map((a, i) => (
                            <div key={i} className="bg-[#0a0a12] rounded-lg px-3 py-2.5" data-testid={`approval-item-${i}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="flex items-start gap-2 min-w-0">
                                  <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 mt-1.5 ${a.risk === 'HIGH' || a.risk === 'CRITICAL' ? 'bg-red-400' : a.risk === 'MEDIUM' ? 'bg-[#ffa502]' : 'bg-gray-500'}`} />
                                  <div className="min-w-0">
                                    <p className="text-xs text-white font-medium truncate">
                                      {a.token_symbol} → {a.spender_name ? (
                                        <span className="text-[#00ff88]">{a.spender_name}</span>
                                      ) : a.spender}
                                    </p>
                                    <p className="text-[10px] text-gray-500 mt-0.5">
                                      {a.is_unlimited ? (
                                        <span className="text-red-400 font-bold">Unlimited approval</span>
                                      ) : (
                                        <span>{a.amount}</span>
                                      )}
                                      {a.etherscan_url && (
                                        <a href={a.etherscan_url} target="_blank" rel="noopener noreferrer"
                                           className="ml-2 text-gray-600 hover:text-gray-400 inline-flex items-center gap-0.5">
                                          Etherscan <ExternalLink className="w-2 h-2" />
                                        </a>
                                      )}
                                    </p>
                                    {a.risk_explanation && (
                                      <p className="text-[10px] text-gray-600 mt-0.5 leading-tight">{a.risk_explanation}</p>
                                    )}
                                  </div>
                                </div>
                                <span className={`text-[10px] font-bold px-2 py-0.5 rounded flex-shrink-0 mt-0.5 ${a.risk === 'HIGH' || a.risk === 'CRITICAL' ? 'text-red-400 bg-red-400/10' : a.risk === 'MEDIUM' ? 'text-[#ffa502] bg-[#ffa502]/10' : 'text-gray-500 bg-gray-800'}`}>
                                  {a.risk}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 bg-[#00ff88]/5 border border-[#00ff88]/20 rounded-lg p-3">
                          <ShieldCheck className="w-4 h-4 text-[#00ff88]" />
                          <p className="text-xs text-[#00ff88]">No risky approvals found — wallet looks clean!</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            {/* Position Cards */}
            {walletData.positions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <Wallet className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p className="text-sm">No DeFi positions found on {walletData.chain}</p>
                <p className="text-xs mt-1">Try a different chain or address</p>
              </div>
            ) : (
              <div className="space-y-3" data-testid="positions-list">
                <p className="text-xs text-gray-500 uppercase tracking-wider px-1">
                  LP Positions — click a position to expand details and use IL calculator
                </p>
                {walletData.positions.map((pos, idx) => (
                  <PositionCard key={idx} position={pos} canUseAI={canUseAI} />
                ))}
              </div>
            )}

            {/* Disclaimer */}
            <div className="bg-[#0f0f1a] border border-gray-800 rounded-xl p-3 text-center">
              <p className="text-[10px] text-gray-600">
                IL estimates are based on position type and token pair. For exact IL, entry price history is required.
                AI recommendations are informational only — not financial advice.
              </p>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!walletData && !loading && !error && (
          <div className="text-center py-16 space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-[#00d4ff]/10 border border-[#00d4ff]/20 flex items-center justify-center mx-auto">
              <Wallet className="w-8 h-8 text-[#00d4ff]/50" />
            </div>
            <div>
              <p className="text-white font-medium">Enter a wallet address to get started</p>
              <p className="text-sm text-gray-500 mt-1">See LP positions, IL risk, and get AI exit recommendations</p>
            </div>
            <div className="flex items-center justify-center gap-6 pt-2">
              {[
                { icon: Shield, label: 'IL Risk Rating', color: '#00ff88' },
                { icon: BarChart2, label: 'Position Breakdown', color: '#00d4ff' },
                { icon: Brain, label: 'AI Exit Signal', color: '#ffa502' },
              ].map(({ icon: Icon, label, color }) => (
                <div key={label} className="flex flex-col items-center gap-1.5">
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: color + '15', border: `1px solid ${color}30` }}>
                    <Icon className="w-4.5 h-4.5" style={{ color }} />
                  </div>
                  <span className="text-[10px] text-gray-500 text-center">{label}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
